import { TimeFormat, USER_FORMAT } from '@agilox/ui-common';
import {
	ButtonComponent,
	DropdownDirective,
	DropdownModule,
	IconModule,
	TooltipModule,
} from '@agilox/ui';
import {
	Component,
	effect,
	ElementRef,
	Inject,
	inject,
	input,
	model,
	output,
	signal,
	untracked,
	viewChild,
} from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { TimePickerAmPmPipe } from './pipes/time-picker-am-pm.pipe';
import { TimePickerHourPipe } from './pipes/time-picker-hour.pipe';
import { TimePickerMinutePipe } from './pipes/time-picker-minute.pipe';

const TWENTY_FOUR_HOUR_MAX = 23;
const TWELVE_HOUR_MAX = 12;

@Component({
	selector: 'ui-time-picker',
	templateUrl: './time-picker.component.html',
	imports: [
		IconModule,
		TooltipModule,
		DropdownModule,
		TimePickerAmPmPipe,
		TimePickerHourPipe,
		TimePickerMinutePipe,
		ButtonComponent,
	],
})
export class TimePickerComponent implements FormValueControl<string> {
	@Inject(USER_FORMAT) private readonly userFormat = inject(USER_FORMAT);

	public timeFormat = this.userFormat().time;

	private readonly twelveHourPattern = /^((0?[1-9])|1[0-2]):([0-5][0-9])\s(am|pm)$/;

	/**
	 * Will show a checkmark icon and color the border in green
	 * if the input is valid.
	 */
	showWhenValid = input<boolean>(false);
	placeholder = input<string>('');
	id = input<string>('');
	readOnlyInput = input<boolean>(false);
	icon = input<string>('clock');
	invalid = input<boolean>(false);
	touched = input<boolean>(false);
	disabled = input<boolean>(false);

	maxHours = input<number>(23);
	maxMinutes = input<number>(59);
	minuteJumps = input<number>(1);

	/**
	 * If false, the time will be treated as a time of day
	 * If false, it will be treated as a time length e.g 8 hours
	 */
	useAsTimeLength = input<boolean>(false);

	// if true, the maxHours will be used as the max time
	useMaxHoursAsMaxTime = input<boolean>(false);

	// Always output in 24h format
	valueChanged = output<string>();
	touch = output<void>();

	value = model<string>('');

	dropdownContent = viewChild<ElementRef>('dropdownContent');

	/** What the input shows: 12h when that format is active, 24h otherwise. */
	displayValue = signal<string>('');

	focused = signal<boolean>(false);
	dropdownOpened = signal(false);

	twentyFourHourValue = signal('');

	input = viewChild<ElementRef | undefined>('inputElement');
	dropdown = viewChild(DropdownDirective);

	constructor() {
		effect(() => {
			const value = this.value();
			untracked(() => this.showValue(value));
		});
	}

	/** The field writing in; never written back, so the display cannot echo. */
	private showValue(value: string): void {
		if (!this.isInputTimeValid(value)) {
			return;
		}

		this.twentyFourHourValue.set(value ?? '');
		this.displayValue.set(
			this.is12HourTimeActive() ? this.twentyFourToTwelve(value) : (value ?? '')
		);
	}

	private commit(raw: string): void {
		const displayed = this.is12HourTimeActive() ? raw : this.doMask(raw);

		const parsed = this.is12HourTimeActive()
			? this.validateAndParse12hrTime(displayed)
			: this.validateAndParse24hrTime(displayed);
		if (parsed === null) {
			this.displayValue.set(displayed);
			return;
		}

		this.displayValue.set(parsed);

		const twentyFour = this.is12HourTimeActive() ? this.twelveToTwentyFour(parsed) : parsed;
		this.twentyFourHourValue.set(twentyFour);
		this.value.set(twentyFour);
		this.touch.emit();
		this.valueChanged.emit(twentyFour);
	}

	onInput(event: Event): void {
		const element = event.target as HTMLInputElement;
		this.commit(element.value);

		if (element.value !== this.displayValue()) {
			element.value = this.displayValue();
		}
	}

	private is12HourTimeActive() {
		return this.timeFormat === TimeFormat.TWELVE_HOUR && !this.useAsTimeLength();
	}

	private validateAndParse12hrTime(value: string | null): string | null {
		if (value === null) {
			return null;
		}
		if (!this.twelveHourPattern.test(value)) {
			return null;
		}
		const [time, amPm] = value.split(' ');
		const [hours, minutes] = time.split(':').map(Number);
		if (hours > 12 || minutes > 59) {
			return null;
		}
		return value;
	}

	onFocusOut() {
		this.focused.set(false);
		this.autoFormatTime();
	}

	/**
	 * Auto formats the time input
	 * Cases:
	 * 	1 Character: 0H -> HH:00
	 * 	2 Characters: HH -> HH:00
	 * 	3 Characters: HH: -> HH:M0
	 * 	3 Characters but Character > 5 -> HH:0M
	 * @private
	 */
	private autoFormatTime() {
		if (!this.displayValue()) {
			return;
		}
		if (/[^\d:]/.test(this.displayValue())) {
			return;
		}

		const val = this.displayValue()
			.split('')
			.filter((c: string) => c !== ':');
		if (!val.length) {
			return;
		}
		switch (val.length) {
			case 1:
				this.commit(`0${val}:00`);
				break;
			case 2:
				this.commit(`${val}:00`);
				break;
			case 3:
				if (Number(val[2]) > 5) {
					this.commit(`${val[0]}${val[1]}:0${val[2]}`);
				} else {
					this.commit(`${val[0]}${val[1]}:${val[2]}0`);
				}
				break;
		}
	}

	openDropdown() {
		if (!this.dropdownOpened()) {
			this.dropdown()?.openDropdown();
			setTimeout(() => this.dropdownContent()?.nativeElement.focus(), 0);
		}
	}

	twelveToTwentyFour(value: string): string {
		let [time, amPm] = value.split(' ');
		let [hours, minutes] = time.split(':').map(Number);
		if (amPm === 'pm' && hours < 12) {
			hours += 12;
		}
		if (amPm === 'am' && hours === 12) {
			hours = 0;
		}
		return `${this.pad(hours)}:${this.pad(minutes)}`;
	}

	twentyFourToTwelve(value: string): string {
		if (!value) {
			return '';
		}

		let [hours, minutes] = value.split(':').map(Number);
		let amPm = hours < 12 ? 'am' : 'pm';
		if (hours === 0) {
			hours = 12;
		} else if (hours > 12) {
			hours -= 12;
		}
		return `${this.pad(hours)}:${this.pad(minutes)} ${amPm}`;
	}

	onDropdownStateChange(opened: boolean) {
		this.focused.set(opened);
		this.dropdownOpened.set(opened);
	}

	setTimeViaArrows(type: 'hours' | 'minutes', direction: 'up' | 'down') {
		if (!this.displayValue()) {
			this.displayValue.set('00:00');
		}

		const [rawHours, rawMinutes] = this.displayValue().split(':');

		let hours = Number(rawHours);
		let minutes = Number(rawMinutes.includes(' ') ? rawMinutes.split(' ')[0] : rawMinutes);

		if (type === 'hours') {
			// special case for 12 hour format
			const currentHours = hours;

			let maxHours = this.maxHours();
			if (this.maxHours() === TWENTY_FOUR_HOUR_MAX && this.is12HourTimeActive()) {
				maxHours = TWELVE_HOUR_MAX;
			}
			hours = this.adjustValue(hours, direction, maxHours, 1);

			if (
				hours === TWELVE_HOUR_MAX &&
				currentHours < TWELVE_HOUR_MAX &&
				direction === 'up' &&
				this.is12HourTimeActive()
			) {
				hours = 0;
			}
		} else {
			minutes = this.adjustValue(minutes, direction, this.maxMinutes(), this.minuteJumps());
		}

		if (this.useMaxHoursAsMaxTime() && hours === this.maxHours() && minutes > 0) {
			minutes = 0;
		}
		let val = `${this.pad(hours)}:${this.pad(minutes)}`;

		if (this.is12HourTimeActive()) {
			val = this.twentyFourToTwelve(val);
		}

		this.commit(val);
	}

	handleAmPmToggle() {
		const time = this.displayValue().split(' ')[0];
		let amPm = this.displayValue().split(' ')[1];
		if (amPm === 'am') {
			amPm = 'pm';
		} else {
			amPm = 'am';
		}
		this.commit(`${time} ${amPm}`);
	}

	private adjustValue(value: number, direction: 'up' | 'down', max: number, time: number): number {
		if (direction === 'up') {
			return value === max ? value - max : value + time;
		} else {
			return value === 0 ? max : value - time;
		}
	}

	private pad(value: number): string {
		return value.toString().padStart(2, '0');
	}

	private doMask(value: string | null): string {
		if (!value) {
			return '';
		}

		if (value.includes(':')) {
			return value.replace(/[^\d:](?!:)/g, '').slice(0, 5);
		}

		const sanitized = value.replace(/[^\d]/g, '');

		const dotPositions: number[] = [2];
		let result: string = '';
		for (let i = 0; i < sanitized.length; i++) {
			if (dotPositions.includes(i) && !(i > 0 && sanitized[i - 1] === ':')) {
				result += ':';
			}
			result += sanitized[i];
		}
		return result.slice(0, 5);
	}

	private validateAndParse24hrTime(value: string | null): string | null {
		if (value?.length !== 5) {
			return null;
		}
		const [hours, minutes] = value.split(':').map(Number);

		const newHours: number = Math.min(Math.max(hours, 0), 23);
		const newMinutes: number = Math.min(Math.max(minutes, 0), 59);
		return `${this.pad(newHours)}:${this.pad(newMinutes)}`;
	}

	private isInputTimeValid(value: any): boolean {
		if (typeof value === 'undefined' || value === null || value === '') {
			return true;
		}

		if (typeof value !== 'string') {
			return false;
		}

		return (
			/^\d{2}:\d{2}$/.test(value) ||
			(this.is12HourTimeActive() && this.twelveHourPattern.test(value))
		);
	}

	protected readonly TimeFormat = TimeFormat;
}
